'use client';

import React, { useEffect, useRef, useState } from 'react';
import { FiMapPin } from 'react-icons/fi';

/**
 * The composer's chips: the shopper's region, and their budget.
 *
 * The budget chip IS the budget's only control. It shows the number the search
 * heard ("≤ $900", typed in the query or said in the conversation), and
 * tapping it turns the chip into a field: type a number, press return.
 * Emptying it removes the budget. Before any number it reads "Budget". No
 * sheet, no brackets, no slider: the feature is the number.
 */

/** A typed amount in whole units, up to 100,000. Empty means "no budget". */
export const parseBudgetDraft = (draft: string): number | null | undefined => {
  const digits = draft.replace(/[^\d]/g, '');
  if (!digits) return null;
  const value = Number.parseInt(digits, 10);
  return Number.isFinite(value) && value > 0 && value <= 100_000 ? value : undefined;
};

interface SearchBarPillsProps {
  /** Region label e.g. "Québec" or null */
  regionLabel?: string | null;
  /** Translated pill label for region e.g. "Region" / "Région" */
  regionPillLabel: string;
  /** Called when user clicks the region chip */
  onRegionClick?: () => void;
  /** Show the budget chip (a search is under way and nothing is loading). */
  showBudget?: boolean;
  /** The number, formatted: "≤ 1 500 $". Null before any number is set. */
  budgetLabel?: string | null;
  /** The ceiling in major units, to seed the field. */
  budgetMax?: number | null;
  /** Chip text before any number ("Budget"). */
  budgetEmptyLabel: string;
  /** Chip aria-label, for whichever state it is in. */
  budgetAriaLabel: string;
  /** The field's aria-label. */
  budgetFieldLabel: string;
  /** The currency symbol, placed the way the locale writes it ("$900", "900 $"). */
  currency: { symbol: string; symbolFirst: boolean };
  /** A typed number, or null to remove the budget. */
  onBudgetCommit?: (max: number | null) => void;
  /** The field opened or closed: the composer stays mounted while it is open. */
  onBudgetEditingChange?: (editing: boolean) => void;
}

export function SearchBarPills({
  regionLabel,
  regionPillLabel,
  onRegionClick,
  showBudget = false,
  budgetLabel,
  budgetMax,
  budgetEmptyLabel,
  budgetAriaLabel,
  budgetFieldLabel,
  currency,
  onBudgetCommit,
  onBudgetEditingChange,
}: SearchBarPillsProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // Return and blur both close the field; only the first one commits.
  const settledRef = useRef(true);

  useEffect(() => {
    onBudgetEditingChange?.(editing);
  }, [editing, onBudgetEditingChange]);

  // Unmounting mid-edit must not leave the composer held open.
  const editingChangeRef = useRef(onBudgetEditingChange);
  editingChangeRef.current = onBudgetEditingChange;
  useEffect(() => () => editingChangeRef.current?.(false), []);

  // The chip hides while results load: close the field without committing.
  useEffect(() => {
    if (!showBudget) {
      settledRef.current = true;
      setEditing(false);
    }
  }, [showBudget]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  if (!regionLabel && !showBudget) return null;

  const openField = () => {
    setDraft(budgetMax ? String(Math.round(budgetMax)) : '');
    settledRef.current = false;
    setEditing(true);
  };

  const closeField = (commit: boolean) => {
    if (settledRef.current) return;
    settledRef.current = true;
    setEditing(false);
    if (!commit) return;
    const next = parseBudgetDraft(draft);
    // Not a usable number: the budget stays as it was.
    if (next === undefined || next === (budgetMax ?? null)) return;
    onBudgetCommit?.(next);
  };

  return (
    <div className="flex min-w-0 items-center gap-1.5 animate-fade-in-scale">
      {regionLabel && (
        <Chip ariaLabel={`${regionPillLabel}: ${regionLabel}`} onClick={onRegionClick}>
          <FiMapPin className="h-3 w-3 flex-shrink-0" />
          <span className="truncate max-w-[7rem] sm:max-w-[11rem]">{regionLabel}</span>
        </Chip>
      )}
      {showBudget &&
        (editing ? (
          <span className={`${CHIP_CLASS} cursor-text border-cornflowerblue/60 bg-white text-zinc-800 dark:border-cornflowerblue/60 dark:bg-white/10 dark:text-white`}>
            {currency.symbolFirst && (
              <span aria-hidden="true" className="text-zinc-400 dark:text-zinc-500">
                {currency.symbol}
              </span>
            )}
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              enterKeyHint="done"
              aria-label={budgetFieldLabel}
              value={draft}
              // The current number is selected, so typing replaces it.
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                  // Never let the composer take this key: Return here sets the
                  // budget, it does not send the search.
                  e.preventDefault();
                  e.stopPropagation();
                  closeField(e.key === 'Enter');
                }
              }}
              onBlur={() => closeField(true)}
              style={{ width: `${Math.max(3, draft.length) + 0.5}ch` }}
              className="min-w-0 bg-transparent font-bold tabular-nums outline-none"
            />
            {!currency.symbolFirst && (
              <span aria-hidden="true" className="text-zinc-400 dark:text-zinc-500">
                {currency.symbol}
              </span>
            )}
          </span>
        ) : (
          <Chip ariaLabel={budgetAriaLabel} onClick={openField}>
            {budgetLabel ? (
              <span className="font-bold tabular-nums whitespace-nowrap">{budgetLabel}</span>
            ) : (
              <span className="truncate">{budgetEmptyLabel}</span>
            )}
          </Chip>
        ))}
    </div>
  );
}

const CHIP_CLASS =
  'inline-flex h-7 min-w-0 select-none items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors';

function Chip({
  ariaLabel,
  onClick,
  children,
}: {
  ariaLabel: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      title={ariaLabel}
      onClick={onClick}
      // Keep the textarea focused: on phones the chips live in a footer that only
      // exists while the bar has focus, so a blur here would unmount the chip
      // between mousedown and click.
      onMouseDown={(e) => e.preventDefault()}
      className={`${CHIP_CLASS} cursor-pointer border-zinc-200/70 bg-zinc-50/80 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-100 hover:text-zinc-800 dark:border-white/10 dark:bg-white/5 dark:text-zinc-300 dark:hover:border-white/20 dark:hover:bg-white/10 dark:hover:text-white`}
    >
      {children}
    </button>
  );
}
